import { ExternalLink, Pause, Play, RotateCcw, Timer } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';
import type { StageTimer as StageTimerProps } from '@/types';
import { useStageAction } from './useStageAction';

const DEFAULT_DURATION_SECONDS = 300;
const STORAGE_KEY = 'stage-timer:reset-seconds';
// One tap for the usual pitch lengths, in minutes (the length dialog's buttons).
const PRESET_MINUTES = [1, 2, 3, 4, 5, 6, 8, 10];

// m:ss, with a leading minus once the clock has run past zero (as the room's screen
// shows it).
function formatSeconds(total: number, overtime = false): string {
  const s = Math.max(0, Math.floor(total));
  const m = Math.floor(s / 60);
  const r = s % 60;
  return `${overtime ? '-' : ''}${m}:${r.toString().padStart(2, '0')}`;
}

export function StageTimer({
  eventSlug,
  stageId,
  stageSlug,
  timer,
}: {
  eventSlug: string;
  stageId: string;
  stageSlug: string;
  timer: StageTimerProps | null;
}) {
  const { pending, error, run } = useStageAction();
  const [resetSeconds, setResetSeconds] = useState<number>(
    DEFAULT_DURATION_SECONDS,
  );
  const [now, setNow] = useState<number>(() => Date.now());
  const [lengthOpen, setLengthOpen] = useState(false);

  useEffect(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const parsed = Number.parseInt(stored, 10);
        if (Number.isFinite(parsed) && parsed > 0) setResetSeconds(parsed);
      }
    } catch {}
  }, []);

  useEffect(() => {
    if (!timer?.endsAt) return;
    const id = setInterval(() => setNow(Date.now()), 250);
    return () => clearInterval(id);
  }, [timer?.endsAt]);

  const isRunning = !!timer?.endsAt;
  const isStopped =
    !isRunning && timer != null && timer.remainingSeconds != null;

  const leftMs = isRunning
    ? new Date(timer?.endsAt as string).getTime() - now
    : null;
  const isOvertime = leftMs !== null && leftMs <= 0;
  const displaySeconds =
    leftMs !== null
      ? isOvertime
        ? Math.floor(-leftMs / 1000)
        : Math.ceil(leftMs / 1000)
      : isStopped
        ? (timer?.remainingSeconds ?? 0)
        : resetSeconds;
  // Paused part-way through: Start picks up where it left off.
  const isPaused = isStopped && timer?.remainingSeconds !== resetSeconds;

  const timerUrl = (action: string) =>
    `/admin/${eventSlug}/stages/${stageId}/timer/${action}`;

  const persistResetSeconds = (value: number) => {
    setResetSeconds(value);
    try {
      localStorage.setItem(STORAGE_KEY, String(value));
    } catch {}
  };

  const reset = (seconds = resetSeconds) =>
    run('post', timerUrl('reset'), { durationSeconds: seconds });

  // Starting an idle stage parks the clock first, exactly as the action did before it
  // called startStageTimerAction.
  const handleStart = () => {
    if (!timer || timer.remainingSeconds == null) {
      run('post', timerUrl('reset'), { durationSeconds: resetSeconds }, () =>
        run('post', timerUrl('start')),
      );
      return;
    }
    run('post', timerUrl('start'));
  };

  const handleStop = () => run('post', timerUrl('stop'));

  // Picking a length while the clock is not running also resets it to that length, so
  // the next Start runs it.
  const choosePreset = (seconds: number) => {
    persistResetSeconds(seconds);
    if (!isRunning) reset(seconds);
  };

  const state = isOvertime
    ? { label: 'Time’s up', className: 'bg-destructive text-white' }
    : isRunning
      ? { label: 'Running', className: 'bg-primary text-primary-foreground' }
      : isPaused
        ? { label: 'Paused', className: 'bg-muted text-foreground' }
        : { label: 'Ready', className: 'bg-muted text-muted-foreground' };

  return (
    <section className="flex flex-col gap-3 border p-3">
      <div className="flex items-center justify-between gap-2">
        <Label>Timer</Label>
        <div className="flex items-center gap-2">
          <span
            className={cn(
              'px-2 py-0.5 font-mono text-[10px] uppercase',
              state.className,
            )}
          >
            {state.label}
          </span>
          <a
            href={`/${eventSlug}/timer/${stageSlug}`}
            title="This stage's timer screen"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-1 text-muted-foreground text-xs hover:text-foreground"
          >
            Screen
            <ExternalLink className="h-3 w-3" />
          </a>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <div
          className={cn(
            'min-w-28 font-bold font-mono text-4xl tabular-nums leading-none',
            isOvertime && 'animate-pulse text-destructive',
            !isRunning && !isStopped && 'text-muted-foreground',
          )}
        >
          {formatSeconds(displaySeconds, isOvertime)}
        </div>
        <div className="flex flex-1 gap-2 sm:flex-none">
          {isRunning ? (
            <Button
              className="h-10 flex-1 rounded-none sm:w-28 sm:flex-none"
              variant="outline"
              onClick={handleStop}
              disabled={pending}
            >
              <Pause className="mr-1 h-4 w-4" />
              Stop
            </Button>
          ) : (
            <Button
              className="h-10 flex-1 rounded-none sm:w-28 sm:flex-none"
              onClick={handleStart}
              disabled={pending}
            >
              <Play className="mr-1 h-4 w-4" />
              {isPaused ? 'Resume' : 'Start'}
            </Button>
          )}
          <Button
            className="h-10 flex-1 rounded-none sm:flex-none"
            variant="outline"
            onClick={() => reset()}
            disabled={pending}
            title={`Reset to ${formatSeconds(resetSeconds)}`}
          >
            <RotateCcw className="mr-1 h-4 w-4" />
            Reset
          </Button>
          <Button
            className="h-10 rounded-none font-mono"
            variant="ghost"
            onClick={() => setLengthOpen(true)}
            disabled={pending}
            title="Change the pitch length"
          >
            <Timer className="mr-1 h-4 w-4" />
            {formatSeconds(resetSeconds)}
          </Button>
        </div>
      </div>

      {error && (
        <p className="bg-destructive/10 p-2 text-destructive text-sm">{error}</p>
      )}

      <Dialog open={lengthOpen} onOpenChange={setLengthOpen}>
        <DialogContent className="rounded-none sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>Pitch length</DialogTitle>
            <DialogDescription>
              {isRunning
                ? 'Applies on the next reset; the running clock is left alone.'
                : 'The clock resets to it right away.'}
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-4 gap-2">
            {PRESET_MINUTES.map((minutes) => {
              const seconds = minutes * 60;
              return (
                <Button
                  key={minutes}
                  variant={resetSeconds === seconds ? 'default' : 'outline'}
                  className="h-11 rounded-none font-mono"
                  onClick={() => {
                    choosePreset(seconds);
                    setLengthOpen(false);
                  }}
                  disabled={pending}
                >
                  {minutes} min
                </Button>
              );
            })}
          </div>
          <div className="flex items-center justify-between gap-2 border-t pt-3">
            <Button
              variant="outline"
              className="h-10 rounded-none"
              onClick={() => choosePreset(Math.max(10, resetSeconds - 30))}
              disabled={pending}
            >
              −30s
            </Button>
            <span className="font-bold font-mono text-2xl tabular-nums">
              {formatSeconds(resetSeconds)}
            </span>
            <Button
              variant="outline"
              className="h-10 rounded-none"
              onClick={() => choosePreset(resetSeconds + 30)}
              disabled={pending}
            >
              +30s
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </section>
  );
}
